import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import API_CONFIG, { apiRequest, getApiUrl } from '../config/api';
import SuccessToast from './SuccessToast';
import ConfirmDialog from './ConfirmDialog';

/**
 * 💬 Pantalla de gestión de comentarios de Instagram.
 *
 * Layout: un menú con dos contenedores independientes. Cada uno se abre
 * a pantalla completa y vuelve atrás al menú. Así las reglas de auto-DM
 * no conviven con el listado de publicaciones ni con sus comentarios.
 *
 *   menú  →  Publicaciones (dos columnas)  │  Reglas de auto-DM
 *            [← Atrás]                     │  [← Atrás]
 *
 * Flujo de control de acceso (defensa en profundidad):
 *   1) La ruta está envuelta en <RequireAuth>: ya garantiza sesión válida.
 *   2) Antes de mostrar nada, llamamos a /comments/permissions?botId=...
 *      Si el backend dice canView=false, mostramos "Sin acceso" y NO se
 *      cargan publicaciones ni comentarios.
 *   3) Cada acción (responder, ocultar, borrar) revalida en backend; el
 *      objeto `permissions` aquí sólo controla qué botones se pintan.
 */
function InstagramCommentsManager({ botId: botIdProp } = {}) {
  // El componente puede usarse:
  //  - como página dedicada (/dashboard/instagram/:botId/comments) → useParams
  //  - inline dentro del dashboard como un tab → prop `botId`
  // Cuando ambos existen, manda la prop (uso inline tiene prioridad).
  const params = useParams();
  const rawBotId = botIdProp != null ? botIdProp : params?.botId;
  const numericBotId = Number(rawBotId);

  // Responsive: en móvil cambiamos el layout a vertical y compactamos los
  // elementos del header. Como el componente usa inline-styles (no CSS),
  // detectamos el breakpoint con matchMedia y aplicamos estilos derivados.
  const isMobile = useIsMobile();

  const [permissions, setPermissions] = useState(null);
  const [permissionsError, setPermissionsError] = useState(null);

  const [media, setMedia] = useState([]);
  const [mediaError, setMediaError] = useState(null);
  const [mediaLoading, setMediaLoading] = useState(false);

  const [selectedMediaId, setSelectedMediaId] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsError, setCommentsError] = useState(null);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const [replyDraft, setReplyDraft] = useState({});
  const [actionInFlight, setActionInFlight] = useState(null);
  const [openReplyId, setOpenReplyId] = useState(null);

  // Filtros + búsqueda local sobre la página actual de comentarios.
  // Como el backend pagina (size=50) los filtros aplican sólo al lote
  // visible; suficiente para la mayoría de publicaciones.
  const [commentSearch, setCommentSearch] = useState('');
  const [commentFilter, setCommentFilter] = useState('all'); // all | unreplied | hidden

  // Tres vistas: el menú no carga nada; cada contenedor pide solo lo suyo.
  const [igView, setIgView] = useState('menu'); // 'menu' | 'publications' | 'rules'
  const [rulesScreen, setRulesScreen] = useState('list'); // 'list' | 'form'
  const [rules, setRules] = useState([]);
  const [rulesError, setRulesError] = useState(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleForm, setRuleForm] = useState(emptyRuleForm());
  const [rulesActionInFlight, setRulesActionInFlight] = useState(null);
  const [ruleToast, setRuleToast] = useState(null);
  const [ruleErrorToast, setRuleErrorToast] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  // ─── 1) Permisos ─────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!Number.isFinite(numericBotId) || numericBotId <= 0) {
        setPermissionsError('botId inválido');
        return;
      }
      try {
        const res = await apiRequest(
          getApiUrl(API_CONFIG.INSTAGRAM.COMMENTS_PERMISSIONS(numericBotId))
        );
        if (cancelled) return;
        if (!res.ok) {
          setPermissionsError(`No se pudieron obtener permisos (HTTP ${res.status})`);
          return;
        }
        const data = await res.json();
        setPermissions(data);
      } catch (err) {
        if (!cancelled) setPermissionsError('Error de red comprobando permisos');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [numericBotId]);

  // ─── 2) Publicaciones ────────────────────────────────────────────────────
  // `silent`: usado en el reintento automático para no parpadear el spinner.
  // El primer fetch sí muestra "Cargando publicaciones…", el reintento NO.
  const loadMedia = useCallback(async ({ silent = false } = {}) => {
    if (!permissions || !permissions.canView) return [];
    if (!silent) {
      setMediaLoading(true);
      setMediaError(null);
    }
    try {
      const res = await apiRequest(
        getApiUrl(API_CONFIG.INSTAGRAM.MEDIA_LIST(numericBotId, 0, 20))
      );
      if (!res.ok) {
        setMediaError(`HTTP ${res.status}`);
        setMedia([]);
        return [];
      }
      const data = await res.json();
      const list = Array.isArray(data?.content) ? data.content : [];
      setMedia(list);
      if (list.length > 0) {
        setSelectedMediaId((prev) => prev || list[0].igMediaId);
      }
      return list;
    } catch (err) {
      setMediaError('Error de red cargando publicaciones');
      setMedia([]);
      return [];
    } finally {
      if (!silent) setMediaLoading(false);
    }
  }, [numericBotId, permissions]);

  // Carga inicial con reintento automático.
  //
  // Justo después del OAuth, Meta puede tardar 2-5 s en habilitar el token
  // recién creado para listar `/me/media`. El backend ya hace un warm-up
  // dentro del callback OAuth (ver `InstagramOAuthService.warmUpMediaCache`),
  // pero si esa primera llamada también pilla a Meta "frío", el listado
  // local de BD queda vacío hasta que alguien refresca.
  //
  // Para que el usuario no tenga que pulsar "Refrescar" manualmente, si la
  // primera carga devuelve `[]` reintentamos en silencio una sola vez con
  // un delay corto. No reintentamos indefinidamente: si tras dos intentos
  // sigue vacío, probablemente la cuenta no tiene publicaciones (caso
  // legítimo) y mostrar "No hay publicaciones aún" es lo correcto.
  useEffect(() => {
    if (igView !== 'publications' && igView !== 'rules') return;
    let cancelled = false;
    let retryTimer = null;
    (async () => {
      const list = await loadMedia();
      if (cancelled || (Array.isArray(list) && list.length > 0)) return;
      // Sólo reintentar si no hubo error explícito; si hay 4xx/5xx el
      // problema no se arregla esperando.
      retryTimer = setTimeout(async () => {
        if (cancelled) return;
        await loadMedia({ silent: true });
      }, 2500);
    })();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [igView, loadMedia]);

  // ─── 3) Comentarios ──────────────────────────────────────────────────────
  const loadComments = useCallback(async () => {
    if (!permissions || !permissions.canView || !selectedMediaId) {
      setComments([]);
      return;
    }
    setCommentsLoading(true);
    setCommentsError(null);
    try {
      const res = await apiRequest(
        getApiUrl(API_CONFIG.INSTAGRAM.COMMENTS_LIST(numericBotId, selectedMediaId, 0, 50))
      );
      if (!res.ok) {
        setCommentsError(`HTTP ${res.status}`);
        setComments([]);
        return;
      }
      const data = await res.json();
      setComments(Array.isArray(data?.content) ? data.content : []);
    } catch (err) {
      setCommentsError('Error de red cargando comentarios');
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, [numericBotId, permissions, selectedMediaId]);

  useEffect(() => {
    if (igView !== 'publications') return;
    loadComments();
  }, [igView, loadComments]);

  // ─── 4) Acciones ─────────────────────────────────────────────────────────
  const submitReply = async (commentId) => {
    const text = (replyDraft[commentId] || '').trim();
    if (!text) return;
    setActionInFlight(`reply:${commentId}`);
    try {
      const res = await apiRequest(getApiUrl(API_CONFIG.INSTAGRAM.REPLY_COMMENT(commentId)), {
        method: 'POST',
        body: JSON.stringify({ botId: numericBotId, text }),
      });
      if (!res.ok) {
        setRuleToast(null);
        setRuleErrorToast('Error respondiendo: HTTP ' + res.status);
        return;
      }
      setReplyDraft((d) => ({ ...d, [commentId]: '' }));
      setOpenReplyId(null);
      await loadComments();
    } finally {
      setActionInFlight(null);
    }
  };

  const toggleHidden = async (commentId, currentlyHidden) => {
    setActionInFlight(`hide:${commentId}`);
    try {
      const res = await apiRequest(getApiUrl(API_CONFIG.INSTAGRAM.HIDE_COMMENT(commentId)), {
        method: 'POST',
        body: JSON.stringify({ botId: numericBotId, hidden: !currentlyHidden }),
      });
      if (!res.ok) {
        setRuleToast(null);
        setRuleErrorToast('Error ocultando: HTTP ' + res.status);
        return;
      }
      await loadComments();
    } finally {
      setActionInFlight(null);
    }
  };

  const removeComment = async (commentId) => {
    setPendingDelete(null);
    setActionInFlight(`delete:${commentId}`);
    try {
      const res = await apiRequest(
        getApiUrl(API_CONFIG.INSTAGRAM.DELETE_COMMENT(commentId, numericBotId)),
        { method: 'DELETE' }
      );
      if (!res.ok) {
        setRuleToast(null);
        setRuleErrorToast('Error borrando: HTTP ' + res.status);
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (body && body.success === false) {
        setRuleToast(null);
        setRuleErrorToast('No se pudo borrar el comentario en Instagram. Inténtalo de nuevo.');
        return;
      }
      setRuleErrorToast(null);
      setRuleToast('Comentario borrado');
      await loadComments();
    } finally {
      setActionInFlight(null);
    }
  };

  // ─── 4b) Reglas de auto-DM ───────────────────────────────────────────────
  const loadRules = useCallback(async () => {
    if (!permissions || !permissions.canView) return;
    setRulesLoading(true);
    setRulesError(null);
    try {
      const res = await apiRequest(getApiUrl(API_CONFIG.INSTAGRAM.AUTO_DM_LIST(numericBotId)));
      if (!res.ok) {
        setRulesError(`HTTP ${res.status}`);
        setRules([]);
        return;
      }
      const data = await res.json();
      setRules(Array.isArray(data) ? data : []);
    } catch (err) {
      setRulesError('Error de red cargando reglas');
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [numericBotId, permissions]);

  useEffect(() => {
    if (igView === 'rules') loadRules();
  }, [igView, loadRules]);

  const startEditRule = (rule) => {
    setEditingRuleId(rule.id);
    setRuleForm({
      keyword: rule.keyword || '',
      matchMode: rule.matchMode || 'CONTAINS',
      dmTemplate: rule.dmTemplate || '',
      commentReplyTemplate: rule.commentReplyTemplate || '',
      igMediaId: rule.igMediaId || '',
      active: rule.active !== false,
      // El backend siempre devuelve `buttons` como array (vacío si no
      // hay), pero por defensa aceptamos también undefined/null.
      buttons: Array.isArray(rule.buttons)
        ? rule.buttons.map((b) => ({ title: b.title || '', url: b.url || '' }))
        : [],
    });
    setRulesScreen('form');
  };

  const startNewRule = () => {
    setEditingRuleId(null);
    setRuleForm(emptyRuleForm());
    setRulesScreen('form');
  };

  const closeRuleForm = () => {
    setRulesScreen('list');
    setEditingRuleId(null);
    setRuleForm(emptyRuleForm());
  };

  // ── Helpers de botones del formulario ───────────────────────────────
  // Mantengo la lógica fuera del JSX para que el `submitRuleForm` quede
  // legible y para poder testear con facilidad si en el futuro extraemos
  // el formulario a un subcomponente.
  const addRuleButton = () => {
    setRuleForm((f) => {
      const current = Array.isArray(f.buttons) ? f.buttons : [];
      if (current.length >= MAX_RULE_BUTTONS) return f;
      return { ...f, buttons: [...current, { title: '', url: '' }] };
    });
  };

  const updateRuleButton = (idx, field, value) => {
    setRuleForm((f) => {
      const current = Array.isArray(f.buttons) ? [...f.buttons] : [];
      if (!current[idx]) return f;
      current[idx] = { ...current[idx], [field]: value };
      return { ...f, buttons: current };
    });
  };

  const removeRuleButton = (idx) => {
    setRuleForm((f) => {
      const current = Array.isArray(f.buttons) ? [...f.buttons] : [];
      current.splice(idx, 1);
      return { ...f, buttons: current };
    });
  };

  const submitRuleForm = async (e) => {
    e.preventDefault();
    if (!ruleForm.keyword.trim() || !ruleForm.dmTemplate.trim()) {
      setRuleErrorToast('Keyword y mensaje son obligatorios');
      return;
    }
    // Validamos los botones en cliente para dar feedback inmediato.
    // El backend revalida igualmente y devolverá 400 con mensaje
    // específico si algo se cuela.
    const cleanButtons = (ruleForm.buttons || [])
      .map((b) => ({ title: (b.title || '').trim(), url: (b.url || '').trim() }))
      .filter((b) => b.title || b.url); // ignoramos filas vacías
    for (const b of cleanButtons) {
      if (!b.title || !b.url) {
        setRuleErrorToast('Cada botón debe tener título y URL');
        return;
      }
      if (b.title.length > 20) {
        setRuleErrorToast('El título del botón no puede superar 20 caracteres');
        return;
      }
      if (!/^https:\/\//i.test(b.url)) {
        setRuleErrorToast('La URL del botón debe empezar por https://');
        return;
      }
    }
    setRulesActionInFlight(editingRuleId ? `edit:${editingRuleId}` : 'create');
    try {
      const payload = {
        botId: numericBotId,
        keyword: ruleForm.keyword.trim(),
        matchMode: ruleForm.matchMode,
        dmTemplate: ruleForm.dmTemplate,
        commentReplyTemplate: ruleForm.commentReplyTemplate.trim() || null,
        igMediaId: ruleForm.igMediaId.trim() || null,
        active: !!ruleForm.active,
        buttons: cleanButtons,
      };
      const url = editingRuleId
        ? getApiUrl(API_CONFIG.INSTAGRAM.AUTO_DM_UPDATE(editingRuleId))
        : getApiUrl(API_CONFIG.INSTAGRAM.AUTO_DM_CREATE);
      const res = await apiRequest(url, {
        method: editingRuleId ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setRuleErrorToast(body.message || `Error guardando regla: HTTP ${res.status}`);
        return;
      }
      const wasCreate = !editingRuleId;
      setEditingRuleId(null);
      setRuleForm(emptyRuleForm());
      setRulesScreen('list');
      await loadRules();
      setRuleErrorToast(null);
      setRuleToast(wasCreate ? 'Regla de auto-DM creada' : 'Regla de auto-DM actualizada');
    } catch (e) {
      setRuleErrorToast('Error de conexión al guardar la regla.');
    } finally {
      setRulesActionInFlight(null);
    }
  };

  const removeRule = async (rule) => {
    setPendingDelete(null);
    setRulesActionInFlight(`delete:${rule.id}`);
    try {
      const res = await apiRequest(
        getApiUrl(API_CONFIG.INSTAGRAM.AUTO_DM_DELETE(rule.id, numericBotId)),
        { method: 'DELETE' }
      );
      if (!res.ok) {
        setRuleToast(null);
        setRuleErrorToast(`Error borrando regla: HTTP ${res.status}`);
        return;
      }
      if (editingRuleId === rule.id) {
        setEditingRuleId(null);
        setRuleForm(emptyRuleForm());
      }
      setRuleErrorToast(null);
      setRuleToast('Regla eliminada');
      await loadRules();
    } finally {
      setRulesActionInFlight(null);
    }
  };

  const toggleRuleActive = async (rule) => {
    setRulesActionInFlight(`toggle:${rule.id}`);
    try {
      const res = await apiRequest(
        getApiUrl(API_CONFIG.INSTAGRAM.AUTO_DM_UPDATE(rule.id)),
        {
          method: 'PUT',
          body: JSON.stringify({
            botId: numericBotId,
            keyword: rule.keyword,
            matchMode: rule.matchMode || 'CONTAINS',
            dmTemplate: rule.dmTemplate,
            // Este PUT reenvía la regla entera y el backend reemplaza el campo
            // en cada escritura: sin esta línea, pausar y reactivar una regla
            // borraría su aviso público.
            commentReplyTemplate: rule.commentReplyTemplate || null,
            igMediaId: rule.igMediaId || null,
            active: !rule.active,
            // Re-enviamos los botones existentes para no perderlos en un
            // toggle pausa/activa (el backend reemplaza el campo entero
            // en cada PUT por simplicidad).
            buttons: Array.isArray(rule.buttons) ? rule.buttons : [],
          }),
        }
      );
      if (!res.ok) {
        setRuleToast(null);
        setRuleErrorToast(`Error actualizando regla: HTTP ${res.status}`);
        return;
      }
      await loadRules();
    } finally {
      setRulesActionInFlight(null);
    }
  };

  const canManageRules = !!(permissions && permissions.canView);

  // ─── Derivados ───────────────────────────────────────────────────────────
  const selectedMedia = useMemo(
    () => media.find((m) => m.igMediaId === selectedMediaId) || null,
    [media, selectedMediaId]
  );

  // Filtramos en memoria sobre la página cargada. Es barato y evita un
  // round-trip por cada cambio de filtro/búsqueda.
  const filteredComments = useMemo(() => {
    const q = commentSearch.trim().toLowerCase();
    return comments.filter((c) => {
      if (commentFilter === 'unreplied' && c.replied) return false;
      if (commentFilter === 'hidden' && !c.hidden) return false;
      if (q && !(`${c.text || ''} ${c.fromUsername || ''}`).toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [comments, commentSearch, commentFilter]);

  // ─── 5) Render ───────────────────────────────────────────────────────────
  if (permissionsError) {
    return (
      <div data-testid="ig-comments-error" style={errorBoxStyle}>
        <div style={errorIconStyle}>!</div>
        <h2 style={{ margin: '0 0 8px' }}>No se pudo cargar la pantalla</h2>
        <p style={{ color: '#666', margin: 0 }}>{permissionsError}</p>
      </div>
    );
  }

  if (!permissions) {
    return (
      <div data-testid="ig-comments-loading" style={loadingBoxStyle}>
        <SpinnerKeyframes />
        <div style={spinnerStyle} />
        <span>Cargando permisos…</span>
      </div>
    );
  }

  if (!permissions.canView) {
    return (
      <div data-testid="ig-comments-denied" style={errorBoxStyle}>
        <div style={errorIconStyle}>🔒</div>
        <h2 style={{ margin: '0 0 8px' }}>Sin acceso</h2>
        <p style={{ color: '#666', margin: 0 }}>
          No tienes permiso para gestionar los comentarios de este bot.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="ig-comments-root" style={isMobile ? containerStyleMobile : containerStyle}>
      <SuccessToast message={ruleToast} onDismiss={() => setRuleToast(null)} />
      <SuccessToast message={ruleErrorToast} variant="error" onDismiss={() => setRuleErrorToast(null)} />
      {pendingDelete && (
        <ConfirmDialog
          title={pendingDelete.kind === 'rule' ? 'Eliminar regla' : 'Borrar comentario'}
          note="Esta acción no se puede deshacer."
          confirmLabel={pendingDelete.kind === 'rule' ? 'Sí, eliminar' : 'Sí, borrar'}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => (pendingDelete.kind === 'rule' ? removeRule(pendingDelete.rule) : removeComment(pendingDelete.id))}
        >
          {pendingDelete.kind === 'rule'
            ? <>¿Borrar la regla <strong>"{pendingDelete.rule.keyword}"</strong>?</>
            : '¿Borrar este comentario?'}
        </ConfirmDialog>
      )}
      {/* HEADER */}
      <header style={isMobile ? headerStyleMobile : headerStyleDesktop(igView, rulesScreen)}>
        <div style={headerLeftStyle}>
          {igView !== 'menu' && (
            <button
              type="button"
              data-testid="ig-view-back"
              aria-label="Atrás"
              onClick={() => {
                if (igView === 'rules' && rulesScreen === 'form') {
                  closeRuleForm();
                  return;
                }
                setIgView('menu');
                setRulesScreen('list');
              }}
              style={headerBackButtonStyle}
            >
              ← Atrás
            </button>
          )}
          <div style={headerLogoStyle}>IG</div>
          <div style={{ minWidth: 0 }}>
            <div style={isMobile ? headerTitleStyleMobile : headerTitleStyle}>
              {igView === 'rules' && rulesScreen === 'form'
                ? (editingRuleId ? 'Editar regla' : 'Nueva regla')
                : igView === 'rules'
                  ? 'Reglas de auto-DM'
                  : igView === 'publications'
                    ? 'Publicaciones'
                    : 'Instagram'}
            </div>
            <div style={headerSubtitleStyle}>
              {igView === 'publications'
                ? `${media.length} ${isMobile ? 'posts' : 'publicaciones'} · ${comments.length} ${isMobile ? 'coment.' : 'comentarios cargados'}`
                : igView === 'rules'
                  ? `${rules.length} ${rules.length === 1 ? 'regla' : 'reglas'}`
                  : (isMobile ? 'Elige una sección' : 'Publicaciones y reglas de auto-DM')}
            </div>
          </div>
        </div>
        {igView !== 'menu' && (
          <div style={headerActionsStyle}>
            <button
              type="button"
              onClick={() => {
                if (igView === 'publications') {
                  loadMedia();
                  loadComments();
                } else {
                  loadRules();
                  loadMedia();
                }
              }}
              style={ghostButtonStyle}
              title="Refrescar"
            >
              {isMobile ? '⟳' : '⟳ Refrescar'}
            </button>
          </div>
        )}
      </header>

      {igView === 'menu' && (
        <InstagramSectionMenu
          isMobile={isMobile}
          onOpenPublications={() => setIgView('publications')}
          onOpenRules={() => {
            setRulesScreen('list');
            setIgView('rules');
          }}
        />
      )}

      {/* CONTENEDOR DE REGLAS — no monta publicaciones ni comentarios */}
      {igView === 'rules' && (
        <section
          data-testid="ig-rules-panel"
          style={isMobile ? rulesPanelStyleMobileFullscreen : rulesPanelStyleDedicated}
        >
          {rulesScreen === 'list' && (
            <>
          <div style={rulesPanelHeaderStyle}>
            <div>
              <h3 style={{ margin: 0, fontSize: 16 }}>Reglas de auto-DM</h3>
              <p style={rulesHelpStyle}>
                Cuando alguien comente algo que coincida con la palabra clave, se
                le enviará automáticamente un mensaje directo. Usa{' '}
                <code style={codeStyle}>{'{username}'}</code> y{' '}
                <code style={codeStyle}>{'{text}'}</code> para personalizar.
              </p>
            </div>
            <div style={rulesHeaderActionsStyle}>
              <span style={pillNeutralStyle}>{rules.length} {rules.length === 1 ? 'regla' : 'reglas'}</span>
              {canManageRules && (
                <button
                  type="button"
                  data-testid="ig-rule-new"
                  onClick={startNewRule}
                  style={newRuleButtonStyle}
                >
                  Nueva regla
                </button>
              )}
            </div>
          </div>

          {rulesError && <div style={errorBannerStyle}>{rulesError}</div>}
          {rulesLoading && <div style={mutedStyle}>Cargando reglas…</div>}

          {!rulesLoading && rules.length === 0 && (
            <div data-testid="ig-rules-empty" style={emptyStateStyle}>
              Aún no hay reglas configuradas.
            </div>
          )}

          {rules.length > 0 && (
            <ul data-testid="ig-rules-list" style={cleanListStyle}>
              {rules.map((r) => (
                <li key={r.id} data-testid={`ig-rule-${r.id}`} style={ruleItemStyle}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={ruleKeywordStyle}>{r.keyword}</span>
                    <span style={pillModeStyle}>{prettyMode(r.matchMode)}</span>
                    {r.igMediaId && (
                      <span style={pillNeutralStyle}>
                        post …{String(r.igMediaId).slice(-6)}
                      </span>
                    )}
                    {r.active ? (
                      <span style={pillSuccessStyle}>● activa</span>
                    ) : (
                      <span style={pillWarnStyle}>⏸ pausada</span>
                    )}
                  </div>
                  <div style={ruleTemplateStyle}>{r.dmTemplate}</div>
                  {r.commentReplyTemplate && (
                    <div style={ruleCommentReplyStyle}>💬 {r.commentReplyTemplate}</div>
                  )}
                  {/* Preview de los botones CTA: replica visualmente lo que
                      el usuario final verá en el DM. No son interactivos
                      (es sólo preview), por eso usamos <a> con onClick
                      preventDefault salvo que el admin abra explícitamente
                      el enlace en una nueva pestaña. */}
                  {Array.isArray(r.buttons) && r.buttons.length > 0 && (
                    <div data-testid={`ig-rule-buttons-${r.id}`} style={ruleButtonsPreviewStyle}>
                      {r.buttons.map((b, i) => (
                        <a
                          key={i}
                          href={b.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={b.url}
                          style={rulePreviewButtonStyle}
                        >
                          🔗 {b.title}
                        </a>
                      ))}
                    </div>
                  )}
                  {canManageRules && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        data-testid={`ig-rule-edit-${r.id}`}
                        onClick={() => startEditRule(r)}
                        style={ghostButtonStyle}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        data-testid={`ig-rule-toggle-${r.id}`}
                        onClick={() => toggleRuleActive(r)}
                        disabled={rulesActionInFlight === `toggle:${r.id}`}
                        style={ghostButtonStyle}
                      >
                        {r.active ? 'Pausar' : 'Activar'}
                      </button>
                      <button
                        type="button"
                        data-testid={`ig-rule-delete-${r.id}`}
                        onClick={() => setPendingDelete({ kind: 'rule', rule: r })}
                        disabled={rulesActionInFlight === `delete:${r.id}`}
                        style={dangerGhostButtonStyle}
                      >
                        Borrar
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

            </>
          )}

          {rulesScreen === 'form' && canManageRules && (
            <form data-testid="ig-rule-form" onSubmit={submitRuleForm} style={ruleFormStyle}>
              <h4 style={{ margin: 0, fontSize: 14, color: '#555' }}>
                {editingRuleId ? '✎ Editar regla' : '+ Nueva regla'}
              </h4>
              <div style={formRowStyle}>
                <label style={ruleLabelStyle}>
                  <span>Palabra clave</span>
                  <input
                    type="text"
                    data-testid="ig-rule-keyword"
                    value={ruleForm.keyword}
                    onChange={(e) => setRuleForm((f) => ({ ...f, keyword: e.target.value }))}
                    required
                    maxLength={255}
                    placeholder="precio, info, comprar…"
                    style={inputStyle}
                  />
                </label>
                <label style={ruleLabelStyle}>
                  <span>Coincidencia</span>
                  <select
                    data-testid="ig-rule-match-mode"
                    value={ruleForm.matchMode}
                    onChange={(e) => setRuleForm((f) => ({ ...f, matchMode: e.target.value }))}
                    style={inputStyle}
                  >
                    <option value="CONTAINS">Contiene</option>
                    <option value="EQUALS">Igual a</option>
                    <option value="STARTS_WITH">Empieza por</option>
                  </select>
                </label>
              </div>
              <label style={ruleLabelStyle}>
                <span>Limitar a una publicación (opcional)</span>
                <select
                  data-testid="ig-rule-media"
                  value={ruleForm.igMediaId}
                  onChange={(e) => setRuleForm((f) => ({ ...f, igMediaId: e.target.value }))}
                  style={inputStyle}
                >
                  <option value="">— Cualquier publicación —</option>
                  {media.map((m) => (
                    <option key={m.igMediaId} value={m.igMediaId}>
                      {(m.caption || '(sin texto)').slice(0, 60)}
                    </option>
                  ))}
                </select>
              </label>
              <label style={ruleLabelStyle}>
                <span>
                  Mensaje a enviar{' '}
                  <strong data-testid="ig-rule-template-limit">
                    (máx. {MAX_DM_TEXT_WITH_BUTTONS} caracteres si incluyes al menos un botón)
                  </strong>
                </span>
                <textarea
                  data-testid="ig-rule-template"
                  rows={3}
                  value={ruleForm.dmTemplate}
                  onChange={(e) => setRuleForm((f) => ({ ...f, dmTemplate: e.target.value }))}
                  maxLength={1000}
                  placeholder="¡Hola @{username}! Te paso info por DM 💜"
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  required
                />
              </label>
              <label style={ruleLabelStyle}>
                <span>Aviso público en el comentario (opcional)</span>
                <textarea
                  data-testid="ig-rule-comment-reply"
                  rows={2}
                  value={ruleForm.commentReplyTemplate}
                  onChange={(e) =>
                    setRuleForm((f) => ({ ...f, commentReplyTemplate: e.target.value }))
                  }
                  maxLength={1000}
                  placeholder="¡Te he escrito por privado, @{username}! 💜"
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <small style={{ color: '#888', fontSize: 11 }}>
                  Se publica como respuesta al comentario, y solo si el DM se envía
                  correctamente. Admite {'{username}'}. Déjalo vacío para no comentar.
                </small>
              </label>
              {/* Botones CTA (web_url). Hasta 3 por regla — límite del
                  generic template de Instagram. Cada botón se renderiza
                  en el DM como un CTA debajo del mensaje. */}
              <div style={buttonsBlockStyle}>
                <div style={buttonsHeaderStyle}>
                  <span style={ruleLabelStyle.label || { fontSize: 12, color: '#555' }}>
                    Botones con enlace (opcional · hasta {MAX_RULE_BUTTONS})
                  </span>
                </div>
                {(ruleForm.buttons || []).length === 0 && (
                  <p style={buttonsHelpStyle}>
                    El DM se enviará como texto plano. Añade un botón si quieres incluir
                    un enlace (p. ej. a tu web, catálogo o landing).
                  </p>
                )}
                <div style={addButtonRowStyle}>
                  <button
                    type="button"
                    data-testid="ig-rule-button-add"
                    onClick={addRuleButton}
                    disabled={(ruleForm.buttons || []).length >= MAX_RULE_BUTTONS}
                    style={ghostButtonStyle}
                  >
                    + Añadir botón
                  </button>
                </div>
                {(ruleForm.buttons || []).map((btn, idx) => (
                  <div
                    key={idx}
                    data-testid={`ig-rule-button-row-${idx}`}
                    style={buttonRowStyle}
                  >
                    <input
                      type="text"
                      data-testid={`ig-rule-button-title-${idx}`}
                      value={btn.title}
                      onChange={(e) => updateRuleButton(idx, 'title', e.target.value)}
                      placeholder="Texto (máx. 20)"
                      maxLength={20}
                      style={{ ...inputStyle, flex: '1 1 140px' }}
                    />
                    <input
                      type="url"
                      data-testid={`ig-rule-button-url-${idx}`}
                      value={btn.url}
                      onChange={(e) => updateRuleButton(idx, 'url', e.target.value)}
                      placeholder="https://..."
                      style={{ ...inputStyle, flex: '2 1 220px' }}
                    />
                    <button
                      type="button"
                      data-testid={`ig-rule-button-remove-${idx}`}
                      onClick={() => removeRuleButton(idx)}
                      style={dangerGhostButtonStyle}
                      title="Quitar botón"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <label style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  data-testid="ig-rule-active"
                  checked={!!ruleForm.active}
                  onChange={(e) => setRuleForm((f) => ({ ...f, active: e.target.checked }))}
                />
                <span>Regla activa</span>
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="submit"
                  data-testid="ig-rule-save"
                  disabled={
                    rulesActionInFlight === 'create' ||
                    rulesActionInFlight === `edit:${editingRuleId}`
                  }
                  style={primaryButtonStyle}
                >
                  {editingRuleId ? 'Guardar cambios' : 'Crear regla'}
                </button>
              </div>
            </form>
          )}
        </section>
      )}

      {igView === 'publications' && (
      <div
        style={isMobile ? twoColumnStyleMobile : twoColumnStyle}
      >
        {/* COLUMNA IZQUIERDA — PUBLICACIONES */}
        <aside style={isMobile ? mediaColumnStyleMobile : mediaColumnStyle}>
          <div style={columnHeaderStyle}>
            <span style={columnTitleStyle}>Publicaciones</span>
            <span style={pillNeutralStyle}>{media.length}</span>
          </div>
          <div style={isMobile ? mediaScrollStyleMobile : mediaScrollStyle}>
            {mediaLoading && <div style={mutedStyle}>Cargando publicaciones…</div>}
            {mediaError && <div style={errorBannerStyle}>{mediaError}</div>}
            {!mediaLoading && media.length === 0 && (
              <div style={emptyStateStyle}>No hay publicaciones aún.</div>
            )}
            <ul data-testid="ig-media-list" style={cleanListStyle}>
              {media.map((m) => {
                const isSelected = selectedMediaId === m.igMediaId;
                return (
                  <li key={m.igMediaId} style={{ marginBottom: 8 }}>
                    <button
                      type="button"
                      data-testid={`ig-media-${m.igMediaId}`}
                      onClick={() => setSelectedMediaId(m.igMediaId)}
                      style={isSelected ? mediaItemSelectedStyle : mediaItemStyle}
                    >
                      <div style={mediaThumbWrapStyle}>
                        {(m.thumbnailUrl || m.mediaUrl) ? (
                          <img
                            src={m.thumbnailUrl || m.mediaUrl}
                            alt=""
                            style={mediaThumbImgStyle}
                            onError={(e) => {
                              // Si la URL caduca o falla, ocultamos la imagen y
                              // dejamos que se vea el placeholder de fondo.
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span style={{ fontSize: 22, opacity: 0.6 }}>📷</span>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={mediaCaptionStyle}>
                          {(m.caption || '(sin texto)').slice(0, 70)}
                        </div>
                        <div style={mediaMetaStyle}>
                          <span>{m.mediaType || 'POST'}</span>
                          <span>·</span>
                          <span>💬 {m.commentsCount || 0}</span>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* COLUMNA DERECHA — COMENTARIOS */}
        <section style={isMobile ? commentsColumnStyleMobile : commentsColumnStyle}>
          <div style={columnHeaderStyle}>
            <div>
              <div style={columnTitleStyle}>Comentarios</div>
              {selectedMedia && (
                <div style={selectedMediaCaptionStyle}>
                  {(selectedMedia.caption || '(publicación sin texto)').slice(0, 80)}
                </div>
              )}
            </div>
            <span style={pillNeutralStyle}>
              {filteredComments.length}
              {filteredComments.length !== comments.length && ` / ${comments.length}`}
            </span>
          </div>

          {/* Toolbar: búsqueda + filtros */}
          <div style={toolbarStyle}>
            <input
              type="search"
              placeholder="Buscar en comentarios…"
              value={commentSearch}
              onChange={(e) => setCommentSearch(e.target.value)}
              style={searchInputStyle}
              data-testid="ig-comments-search"
            />
            <div style={filterTabsStyle}>
              <FilterTab id="all" current={commentFilter} onClick={setCommentFilter}>
                Todos
              </FilterTab>
              <FilterTab id="unreplied" current={commentFilter} onClick={setCommentFilter}>
                Sin responder
              </FilterTab>
              <FilterTab id="hidden" current={commentFilter} onClick={setCommentFilter}>
                Ocultos
              </FilterTab>
            </div>
          </div>

          <div style={isMobile ? commentsScrollStyleMobile : commentsScrollStyle}>
            {commentsLoading && <div style={mutedStyle}>Cargando comentarios…</div>}
            {commentsError && <div style={errorBannerStyle}>{commentsError}</div>}
            {!commentsLoading && comments.length === 0 && (
              <div data-testid="ig-comments-empty" style={emptyStateStyle}>
                Sin comentarios.
              </div>
            )}
            {!commentsLoading && comments.length > 0 && filteredComments.length === 0 && (
              <div style={emptyStateStyle}>
                Ningún comentario coincide con el filtro actual.
              </div>
            )}

            <ul data-testid="ig-comments-list" style={cleanListStyle}>
              {filteredComments.map((c) => {
                const username = c.fromUsername || c.fromIgUserId || 'desconocido';
                const replyOpen = openReplyId === c.igCommentId;
                return (
                  <li key={c.igCommentId} style={commentItemStyle}>
                    <div style={avatarStyle(username)}>{initialOf(username)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={commentHeaderRowStyle}>
                        <strong style={commentUserStyle}>@{username}</strong>
                        {c.parentCommentId && <span style={pillSubStyle}>respuesta</span>}
                        {c.hidden && <span style={pillWarnStyle}>oculto</span>}
                        {c.replied && (
                          <span style={pillSuccessStyle}>
                            {c.repliedAutomatically ? 'automático' : 'respondido'}
                          </span>
                        )}
                        {c.commentCreatedTime && (
                          <span style={timestampStyle}>{formatRelative(c.commentCreatedTime)}</span>
                        )}
                      </div>
                      <div style={commentTextStyle}>{c.text}</div>

                      <div style={commentActionsStyle}>
                        {permissions.canReply && (
                          <button
                            type="button"
                            onClick={() =>
                              setOpenReplyId((prev) => (prev === c.igCommentId ? null : c.igCommentId))
                            }
                            style={ghostButtonStyle}
                          >
                            {replyOpen ? 'Cancelar' : '💬 Responder'}
                          </button>
                        )}
                        {permissions.canHide && (
                          <button
                            type="button"
                            data-testid={`ig-hide-btn-${c.igCommentId}`}
                            onClick={() => toggleHidden(c.igCommentId, c.hidden)}
                            disabled={actionInFlight === `hide:${c.igCommentId}`}
                            style={ghostButtonStyle}
                          >
                            {c.hidden ? '👁 Mostrar' : '🙈 Ocultar'}
                          </button>
                        )}
                        {permissions.canDelete && (
                          <button
                            type="button"
                            data-testid={`ig-delete-btn-${c.igCommentId}`}
                            onClick={() => setPendingDelete({ kind: 'comment', id: c.igCommentId })}
                            disabled={actionInFlight === `delete:${c.igCommentId}`}
                            style={dangerGhostButtonStyle}
                          >
                            🗑 Borrar
                          </button>
                        )}
                      </div>

                      {permissions.canReply && replyOpen && (
                        <div style={replyBoxStyle}>
                          <textarea
                            data-testid={`ig-reply-input-${c.igCommentId}`}
                            rows={2}
                            value={replyDraft[c.igCommentId] || ''}
                            onChange={(e) =>
                              setReplyDraft((d) => ({ ...d, [c.igCommentId]: e.target.value }))
                            }
                            placeholder={`Responder a @${username}…`}
                            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                          />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => setOpenReplyId(null)}
                              style={ghostButtonStyle}
                            >
                              Cerrar
                            </button>
                            <button
                              type="button"
                              data-testid={`ig-reply-btn-${c.igCommentId}`}
                              onClick={() => submitReply(c.igCommentId)}
                              disabled={
                                actionInFlight === `reply:${c.igCommentId}` ||
                                !((replyDraft[c.igCommentId] || '').trim())
                              }
                              style={primaryButtonStyle}
                            >
                              Enviar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      </div>
      )}
    </div>
  );
}

// ─── Subcomponentes ────────────────────────────────────────────────────────

/**
 * Inyecta una sola vez la regla `@keyframes` que necesita {@link spinnerStyle}.
 * Como NO usamos un fichero CSS dedicado para este componente, la única
 * forma de tener animaciones reusables sin librerías es renderizar un
 * <style> con el keyframe. React lo deja vivir en el DOM y lo retira con el
 * unmount (en este caso da igual: una keyframe global es inocua).
 */
function SpinnerKeyframes() {
  return (
    <style>{`@keyframes ig-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
  );
}

function FilterTab({ id, current, onClick, children }) {
  const active = id === current;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      style={active ? filterTabActiveStyle : filterTabStyle}
    >
      {children}
    </button>
  );
}

function InstagramSectionMenu({ isMobile, onOpenPublications, onOpenRules }) {
  return (
    <nav data-testid="ig-section-menu" style={isMobile ? sectionMenuStyleMobile : sectionMenuStyle}>
      <button
        type="button"
        data-testid="ig-menu-publications"
        onClick={onOpenPublications}
        style={sectionMenuCardStyle}
      >
        <span style={sectionMenuGlyphStyle}>
          <img
            src={MENU_ICON_PUBLICACIONES}
            alt=""
            style={sectionMenuGlyphImgStyle}
          />
        </span>
        <span style={sectionMenuCardBodyStyle}>
          <span style={sectionMenuCardTitleStyle}>Publicaciones</span>
          <span style={sectionMenuCardHintStyle}>
            Comentarios de tus posts: responder, ocultar o borrar.
          </span>
        </span>
        <span style={sectionMenuChevronStyle} aria-hidden="true">→</span>
      </button>
      <button
        type="button"
        data-testid="ig-menu-rules"
        onClick={onOpenRules}
        style={sectionMenuCardStyle}
      >
        <span style={sectionMenuGlyphStyle}>
          <img
            src={MENU_ICON_AUTO_DM}
            alt=""
            style={sectionMenuGlyphImgStyle}
          />
        </span>
        <span style={sectionMenuCardBodyStyle}>
          <span style={sectionMenuCardTitleStyle}>Reglas de auto-DM</span>
          <span style={sectionMenuCardHintStyle}>
            Mensaje automático cuando alguien comenta una palabra clave.
          </span>
        </span>
        <span style={sectionMenuChevronStyle} aria-hidden="true">→</span>
      </button>
    </nav>
  );
}

/**
 * Hook reactivo a `(max-width: 768px)`. Se reevalúa al redimensionar la
 * ventana, así un usuario que rote el móvil ve el layout actualizado al
 * instante. SSR-safe: en entornos sin `window` (tests sin DOM, prerender)
 * devuelve `false`.
 */
function useIsMobile(query = '(max-width: 768px)') {
  const getMatch = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };
  const [matches, setMatches] = useState(getMatch);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    // addEventListener es lo moderno; algunos navegadores antiguos sólo
    // tienen addListener. Cubrimos ambos para que el bundle no rompa en
    // entornos viejos.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    setMatches(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}

// ─── Helpers ───────────────────────────────────────────────────────────────
// Tope que respeta el backend (y el generic template de Instagram). Lo
// duplico aquí para evitar otro request al servidor sólo para conocerlo.
// Si cambia, hay que sincronizarlo con InstagramAutoDmService.MAX_BUTTONS_PER_RULE.
const MAX_RULE_BUTTONS = 3;
// Cap del title del generic template: con botones Instagram no envía el
// texto plano, y Meta recorta esa cabecera a 80. Hay que mantenerlo a
// la par de InstagramCommentService.buildMessagePayload (truncate 80).
const MAX_DM_TEXT_WITH_BUTTONS = 80;
// Fotos Unsplash: diapositivas (publicaciones) y un chat de DM (auto-DM).
const MENU_ICON_PUBLICACIONES = '/ig-menu/publicaciones.jpg';
const MENU_ICON_AUTO_DM = '/ig-menu/auto-dm.jpg';

function emptyRuleForm() {
  return {
    keyword: '',
    matchMode: 'CONTAINS',
    dmTemplate: '',
    commentReplyTemplate: '',
    igMediaId: '',
    active: true,
    buttons: [],
  };
}

function prettyMode(m) {
  switch (m) {
    case 'EQUALS':
      return 'igual a';
    case 'STARTS_WITH':
      return 'empieza por';
    case 'CONTAINS':
    default:
      return 'contiene';
  }
}

function initialOf(name) {
  if (!name) return '?';
  const ch = name.replace(/^@/, '').trim().charAt(0);
  return ch ? ch.toUpperCase() : '?';
}

// Color determinista a partir del username, así cada usuario tiene siempre
// el mismo avatar a lo largo de la sesión.
function colorFor(name) {
  const palette = ['#E1306C', '#833AB4', '#5B51D8', '#3B82F6', '#0EA5A4', '#22C55E', '#F59E0B', '#EF4444'];
  if (!name) return palette[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

function formatRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} d`;
  return d.toLocaleDateString();
}

// ─── Estilos ───────────────────────────────────────────────────────────────
//
// IMPORTANTE: el contenedor es un flex-column de altura `100%`. El menú,
// las publicaciones y las reglas se muestran de uno en uno: cada vista
// ocupa el espacio sobrante y scrollea por dentro.
//
// El padre que monta este componente (registros-main-content / chat-main)
// ya es flex column con height definido, así que `height: 100%` funciona
// correctamente. Cuando se monta como página de ruta directa, el body
// tiene 100vh y este 100% baja por la cadena.
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  // Triple seguro de altura para encajar tanto si el padre es flex (caso
  // normal: `.chat-main` / `.registros-main-content`) como si es bloque
  // (página de ruta directa). `flex:1` dentro de flex, `height:100%` como
  // fallback y `minHeight:0` para permitir que los hijos scrolleen.
  flex: 1,
  height: '100%',
  minHeight: 0,
  padding: 14,
  boxSizing: 'border-box',
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  color: '#1f1f1f',
};
// Variante móvil: padding más fino para aprovechar el ancho.
// Además, en móvil dejamos que el scroll fluya al scroller común de
// `.ig-settings-panel` (definido en App.css bajo el media query 768px).
// Por eso quitamos aquí las restricciones de altura y overflow que
// impedirían al gesto táctil "swipear" hacia abajo dentro de él.
const containerStyleMobile = {
  ...containerStyle,
  padding: 8,
  flex: 'none',
  height: 'auto',
  minHeight: 0,
  overflow: 'visible',
};

const headerStyle = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '14px 18px',
  marginBottom: 12,
  borderRadius: 14,
  background: 'linear-gradient(135deg, #833AB4 0%, #E1306C 50%, #F77737 100%)',
  color: '#fff',
  boxShadow: '0 4px 14px rgba(225, 48, 108, 0.25)',
  flexWrap: 'wrap',
};
// En el menú de escritorio las tarjetas van pegadas a la cabecera.
const headerStyleDesktop = (igView, rulesScreen) => (
  igView === 'menu' || (igView === 'rules' && rulesScreen === 'form')
    ? { ...headerStyle, marginBottom: 0 }
    : headerStyle
);
// Variante móvil: padding y logo más pequeños, gap reducido.
const headerStyleMobile = {
  ...headerStyle,
  padding: '10px 12px',
  gap: 8,
  marginBottom: 8,
  borderRadius: 12,
};
const headerLeftStyle = { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 auto' };
const headerLogoStyle = {
  width: 40, height: 40, borderRadius: 12, flexShrink: 0,
  background: 'rgba(255,255,255,0.2)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 800, letterSpacing: 0.5,
};
const headerTitleStyle = {
  fontSize: 18, fontWeight: 700, lineHeight: 1.1,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const headerTitleStyleMobile = {
  ...headerTitleStyle,
  fontSize: 15,
};
const headerSubtitleStyle = {
  fontSize: 12, opacity: 0.85, marginTop: 2,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
const headerActionsStyle = { display: 'flex', gap: 6, flexWrap: 'wrap' };
const headerBackButtonStyle = {
  flexShrink: 0,
  border: '1px solid rgba(255,255,255,0.45)',
  background: 'rgba(255,255,255,0.16)',
  color: '#fff',
  borderRadius: 10,
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600,
};

const sectionMenuStyle = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  justifyContent: 'flex-start',
  padding: 0,
};
const sectionMenuStyleMobile = {
  ...sectionMenuStyle,
  justifyContent: 'flex-start',
  padding: '4px 0 8px',
  gap: 10,
};
const sectionMenuCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  width: '100%',
  textAlign: 'left',
  padding: '18px 16px',
  border: '1px solid #ececec',
  borderRadius: 16,
  background: '#fff',
  cursor: 'pointer',
  boxShadow: '0 2px 12px rgba(225, 48, 108, 0.06)',
  borderLeft: '4px solid #E1306C',
};
const sectionMenuGlyphStyle = {
  width: 56,
  height: 56,
  flex: '0 0 56px',
  borderRadius: 14,
  overflow: 'hidden',
  background: '#f3f3f5',
};
const sectionMenuGlyphImgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};
const sectionMenuCardBodyStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};
const sectionMenuCardTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: '#1f1f1f',
};
const sectionMenuCardHintStyle = {
  fontSize: 13,
  color: '#666',
  lineHeight: 1.35,
};
const sectionMenuChevronStyle = {
  flexShrink: 0,
  fontSize: 18,
  color: '#E1306C',
  fontWeight: 700,
};

// Errores y carga
const errorBoxStyle = {
  padding: 32, margin: '40px auto', maxWidth: 480,
  border: '1px solid #eee', borderRadius: 16, textAlign: 'center',
  background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
};
const errorIconStyle = {
  width: 56, height: 56, margin: '0 auto 12px', borderRadius: '50%',
  background: '#fde8ee', color: '#E1306C', fontSize: 28,
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
};
const loadingBoxStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
  padding: 32, color: '#666', fontFamily: 'system-ui, sans-serif',
};
const spinnerStyle = {
  width: 18, height: 18, borderRadius: '50%',
  border: '2px solid #ddd', borderTopColor: '#E1306C',
  animation: 'ig-spin 0.8s linear infinite',
};

// Botones
const primaryButtonStyle = {
  border: 'none', borderRadius: 10, padding: '8px 14px',
  background: '#fff', color: '#E1306C', fontWeight: 600, cursor: 'pointer',
  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
};
const ghostButtonStyle = {
  background: '#fff', border: '1px solid #e2e2e2', borderRadius: 8,
  padding: '6px 10px', cursor: 'pointer', fontSize: 13, color: '#333',
};
const dangerGhostButtonStyle = {
  ...ghostButtonStyle, color: '#c0223a', borderColor: '#f1c5ce',
};

// Layout dos columnas con scroll independiente.
// `flex: 1; minHeight: 0` es la clave: hace que ocupe el espacio sobrante
// del flex-column padre y permite que los hijos `overflow-y: auto` realmente
// scrolleen (sin `minHeight: 0` el flex item nunca se contrae y el scroll
// se desactiva).
const twoColumnStyle = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  gap: 12,
  alignItems: 'stretch',
};
// En móvil apilamos verticalmente publicaciones y comentarios en flujo
// natural, sin scrolls internos. Es `.ig-settings-panel` el único
// scroller (App.css @media 768px). Tres áreas de scroll anidadas
// (documento + columna publicaciones + columna comentarios) confunden
// el gesto táctil en iOS/Android y no se puede desplazar hacia abajo.
const twoColumnStyleMobile = {
  ...twoColumnStyle,
  flexDirection: 'column',
  gap: 8,
  flex: 'none',
  minHeight: 0,
  alignItems: 'stretch',
};
const mediaColumnStyle = {
  flex: '0 0 320px',
  // minHeight:0 + flexDirection:column es lo que permite que el hijo
  // mediaScrollStyle (overflow-y:auto) realmente scrollee dentro de la
  // columna sin estirarla.
  minHeight: 0,
  display: 'flex', flexDirection: 'column',
  background: '#fff', border: '1px solid #ececec', borderRadius: 14,
  overflow: 'hidden',
};
// En móvil la "columna" de publicaciones pasa a ser una franja superior
// que ocupa todo el ancho, en flujo natural, sin altura máxima ni
// overflow oculto: la lista crece libremente y el scroll lo hace el
// panel padre.
const mediaColumnStyleMobile = {
  ...mediaColumnStyle,
  flex: '0 0 auto',
  width: '100%',
  borderRadius: 12,
  overflow: 'visible',
};
const commentsColumnStyle = {
  flex: 1, minWidth: 0,
  minHeight: 0,
  display: 'flex', flexDirection: 'column',
  background: '#fff', border: '1px solid #ececec', borderRadius: 14,
  overflow: 'hidden',
};
const commentsColumnStyleMobile = {
  ...commentsColumnStyle,
  borderRadius: 12,
  flex: 'none',
  overflow: 'visible',
};
const columnHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '12px 14px', borderBottom: '1px solid #f0f0f0', gap: 8,
};
const columnTitleStyle = { fontSize: 14, fontWeight: 700, color: '#222' };
const selectedMediaCaptionStyle = {
  fontSize: 12, color: '#666', marginTop: 2,
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  maxWidth: 480,
};
const mediaScrollStyle = {
  flex: 1, overflowY: 'auto', padding: 10,
};
const commentsScrollStyle = {
  flex: 1, overflowY: 'auto', padding: '8px 14px 14px',
};
// Variantes móviles: sin overflow interno para que el scroll pase al
// panel padre (.ig-settings-panel) y el usuario pueda swipear con
// normalidad hacia abajo.
const mediaScrollStyleMobile = {
  ...mediaScrollStyle,
  flex: 'none',
  overflowY: 'visible',
};
const commentsScrollStyleMobile = {
  ...commentsScrollStyle,
  flex: 'none',
  overflowY: 'visible',
};

// Listas
const cleanListStyle = { listStyle: 'none', padding: 0, margin: 0 };

// Items de publicación
const mediaItemStyle = {
  display: 'flex', gap: 10, alignItems: 'flex-start',
  width: '100%', textAlign: 'left',
  padding: 8, border: '1px solid #ececec', borderRadius: 10,
  cursor: 'pointer', background: '#fff', transition: 'all 0.15s',
};
const mediaItemSelectedStyle = {
  ...mediaItemStyle,
  borderColor: '#E1306C',
  background: 'linear-gradient(135deg, rgba(131,58,180,0.06), rgba(225,48,108,0.06))',
  boxShadow: 'inset 0 0 0 1px #E1306C',
};
const mediaThumbWrapStyle = {
  width: 56, height: 56, flex: '0 0 56px',
  borderRadius: 8, overflow: 'hidden',
  background: 'linear-gradient(135deg, #f5f5f5, #ececec)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const mediaThumbImgStyle = { width: '100%', height: '100%', objectFit: 'cover' };
const mediaCaptionStyle = {
  fontSize: 13, color: '#222', lineHeight: 1.35,
  display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
  overflow: 'hidden',
};
const mediaMetaStyle = {
  fontSize: 11, color: '#777', marginTop: 4, display: 'flex', gap: 4,
};

// Toolbar de comentarios
const toolbarStyle = {
  display: 'flex', gap: 8, padding: '10px 14px',
  borderBottom: '1px solid #f0f0f0', alignItems: 'center', flexWrap: 'wrap',
};
const searchInputStyle = {
  flex: 1, minWidth: 160, padding: '8px 12px',
  borderRadius: 8, border: '1px solid #e2e2e2', fontSize: 13,
  outline: 'none',
};
const inputStyle = {
  padding: '8px 10px', borderRadius: 8, border: '1px solid #e2e2e2',
  fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const filterTabsStyle = { display: 'flex', gap: 4, background: '#f7f7f8', borderRadius: 10, padding: 3 };
const filterTabStyle = {
  background: 'transparent', border: 'none', padding: '6px 10px',
  borderRadius: 8, cursor: 'pointer', fontSize: 12, color: '#555',
};
const filterTabActiveStyle = {
  ...filterTabStyle, background: '#fff', color: '#E1306C', fontWeight: 600,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

// Items de comentario
const commentItemStyle = {
  display: 'flex', gap: 10, padding: '12px 4px',
  borderBottom: '1px solid #f3f3f3',
};
const avatarStyle = (name) => ({
  width: 36, height: 36, flex: '0 0 36px', borderRadius: '50%',
  background: colorFor(name),
  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 700, fontSize: 14,
});
const commentHeaderRowStyle = { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' };
const commentUserStyle = { fontSize: 13, color: '#222' };
const commentTextStyle = {
  marginTop: 4, fontSize: 14, lineHeight: 1.4, color: '#1f1f1f',
  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
};
const commentActionsStyle = { marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' };
const replyBoxStyle = {
  marginTop: 8, padding: 8, borderRadius: 10,
  background: '#fafafa', border: '1px solid #eee',
  display: 'flex', flexDirection: 'column', gap: 6,
};
const timestampStyle = { fontSize: 11, color: '#999', marginLeft: 'auto' };

// Pills / badges
const pillBaseStyle = {
  fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 500,
};
const pillNeutralStyle = { ...pillBaseStyle, background: '#f1f1f3', color: '#555' };
const pillSubStyle = { ...pillBaseStyle, background: '#eef2ff', color: '#4f46e5' };
const pillSuccessStyle = { ...pillBaseStyle, background: '#e7f7ec', color: '#1f8a3a' };
const pillWarnStyle = { ...pillBaseStyle, background: '#fff4e5', color: '#a25700' };
const pillModeStyle = { ...pillBaseStyle, background: '#f3e8ff', color: '#6b21a8' };

// Panel reglas como contenedor propio: ocupa el resto del alto y scrollea
// dentro. Ya no comparte pantalla con publicaciones/comentarios.
const rulesPanelStyleDedicated = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  border: '1px solid #ececec', borderRadius: 14, padding: 16,
  marginBottom: 0, background: '#fff',
  boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
};
// Variante móvil: padding más fino; el scroll sigue siendo interno.
const rulesPanelStyleMobileFullscreen = {
  ...rulesPanelStyleDedicated,
  borderRadius: 12, padding: 12,
};
const rulesPanelHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
};
const rulesHeaderActionsStyle = {
  display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap',
};
const newRuleButtonStyle = {
  border: 'none',
  borderRadius: 10,
  padding: '8px 14px',
  background: 'linear-gradient(135deg, #833AB4 0%, #E1306C 100%)',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};
const rulesHelpStyle = { fontSize: 12, color: '#666', marginTop: 4, marginBottom: 8 };
const codeStyle = {
  background: '#f3f3f5', padding: '1px 6px', borderRadius: 4, fontSize: 11,
};
const ruleItemStyle = {
  padding: 12, border: '1px solid #ececec', borderRadius: 10, marginBottom: 8,
  background: '#fdfdfd', display: 'flex', flexDirection: 'column', gap: 8,
};
const ruleKeywordStyle = {
  fontSize: 14, fontWeight: 700, color: '#222',
  background: 'linear-gradient(135deg, #833AB4, #E1306C)',
  WebkitBackgroundClip: 'text', backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};
const ruleTemplateStyle = {
  fontSize: 13, color: '#333', whiteSpace: 'pre-wrap',
  background: '#fafafa', padding: 8, borderRadius: 8,
  borderLeft: '3px solid #E1306C',
};
const ruleCommentReplyStyle = {
  fontSize: 12, color: '#555', whiteSpace: 'pre-wrap',
  background: '#fafafa', padding: 8, borderRadius: 8,
  borderLeft: '3px solid #999',
};
const ruleFormStyle = {
  display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14,
  paddingTop: 14, borderTop: '1px dashed #e2e2e2',
};
const formRowStyle = { display: 'flex', gap: 10, flexWrap: 'wrap' };
const ruleLabelStyle = {
  display: 'flex', flexDirection: 'column', fontSize: 12, gap: 4,
  flex: 1, minWidth: 180, color: '#555',
};
const checkboxRowStyle = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 };

// Botones CTA del formulario de regla
const buttonsBlockStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  padding: 10,
  borderRadius: 10,
  border: '1px dashed #e2e2e2',
  background: '#fafafa',
};
const buttonsHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const buttonsHelpStyle = { fontSize: 12, color: '#777', margin: 0 };
const addButtonRowStyle = { display: 'flex', justifyContent: 'flex-start' };
const buttonRowStyle = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
  flexWrap: 'wrap',
};

// Preview de botones dentro de la tarjeta de cada regla
const ruleButtonsPreviewStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: -2,
};
const rulePreviewButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 10px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  color: '#E1306C',
  background: '#fff',
  border: '1px solid #f1c5ce',
  textDecoration: 'none',
  maxWidth: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

// Otros
const errorBannerStyle = {
  padding: 8, borderRadius: 8, color: '#c0223a',
  background: '#fde8ee', border: '1px solid #f5c4ce', fontSize: 13, marginBottom: 8,
};
const emptyStateStyle = {
  padding: 18, textAlign: 'center', color: '#888', fontSize: 13,
  background: '#fafafa', borderRadius: 8, border: '1px dashed #e2e2e2',
};
const mutedStyle = { color: '#777', fontSize: 13, padding: 8 };

export default InstagramCommentsManager;
